import {
  Component,
  Event,
  type EventEmitter,
  Host,
  Prop,
  h,
} from '@stencil/core';

import { trackEvent } from '../../helpers/tracking';
import type { CssClassMap } from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-button',
  styleUrl: 'cms-button.scss',
  shadow: true,
})
export class CmsButton {
  /**
   * set the tracking parameter event type
   */
  @Prop() dataEventtype: string | undefined;

  /**
   * set the tracking parameter event
   */
  @Prop() dataEvent: string | undefined;

  /**
   * set the tracking parameter uid
   */
  @Prop() dataUid: string | undefined;

  /**
   * set the tracking namespace
   */
  @Prop() namespace: string | undefined;

  /**
   * If true the user cannot interact with the button.
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * set icon inside button
   */
  @Prop() icon: string | undefined;

  /**
   * set icon size inside button like sm, md, lg
   */
  @Prop() iconSize: string | undefined;

  /**
   * set icon color inside button as css property
   */
  @Prop() iconColor: string | undefined;

  /**
   * This attribute instructs browsers to download a URL instead of navigating to
   * it, so the user will be prompted to save it as a local file. If the attribute
   * has a value, it is used as the pre-filled file name in the Save prompt
   * (the user can still change the file name if they want).
   */
  @Prop() download: string | undefined;

  /**
   * Contains a URL or a URL fragment that the hyperlink points to.
   * If this property is set, an anchor tag will be rendered.
   */
  @Prop() href: string | undefined;

  /**
   * Specifies where to display the linked URL.
   * Only applies when an `href` is provided.
   * Special keywords: `"_blank"`, `"_self"`, `"_parent"`, `"_top"`.
   */
  @Prop() target: '_blank' | '_self' | '_parent' | '_top';

  /**
   * Specifies the relationship of the target object to the link object.
   * The value is a space-separated list of [link types](https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types).
   */
  @Prop() rel: string | undefined;

  /**
   * set the button size
   */
  @Prop({ reflect: true }) size?:
    | 'button-xs'
    | 'button-sm'
    | 'button-md'
    | 'button-lg';

  /**
   * The type of the button.
   */
  @Prop() type: 'submit' | 'reset' | 'button' = 'button';

  /**
   * set the button color
   */
  @Prop({ reflect: true }) theme?:
    | 'primary'
    | 'primary-buy'
    | 'secondary'
    | 'secondary-inverted'
    | 'flat'
    | 'flat-inverted';

  /**
   * set the button Label
   */
  @Prop({ reflect: true }) label: string;

  /**
   * set the title of the button or anchor tag
   */
  @Prop() tagTitle: string;

  /**
   * set if the button is focusable
   */
  @Prop() focusable = true;

  /**
   * fired the custom event cmsButtonClick
   */
  @Event({
    eventName: 'cmsButtonClick',
    composed: true,
    bubbles: true,
  })
  cmsButtonClick: EventEmitter;

  /**
   * Emitted when the button has focus.
   */
  @Event()
  ionFocus!: EventEmitter<void>;

  /**
   * Emitted when the button loses focus.
   */
  @Event()
  ionBlur!: EventEmitter<void>;

  private readonly onFocus = () => {
    this.ionFocus.emit();
  };

  private readonly onBlur = () => {
    this.ionBlur.emit();
  };

  private readonly cmsButtonClickHandler = (event: Event) => {
    this.cmsButtonClick.emit();

    /* Tracking */
    if (this.namespace && this.href === undefined) {
      trackEvent(this.namespace);
    }

    const target = event.target as HTMLElement;
    target.blur();
  };

  private tagTypeClassMap(): CssClassMap {
    return {
      [this.theme]: true,
      [this.size]: true,
      'native-button': true,
    };
  }

  private hostClassMap(): CssClassMap {
    return {
      [this.type]: true,
      disabled: this.disabled,
      [this.icon]: this.icon !== undefined,
      [this.size]: this.size !== undefined,
      [this.theme]: this.theme !== undefined,
      'only-icon': this.label === undefined && this.icon !== undefined,
    };
  }

  render() {
    const TagType = this.href === undefined ? 'button' : ('a' as const);
    const tagAttributes =
      TagType === 'button'
        ? { type: this.type, title: this.label ? this.label : this.icon }
        : {
            title: this.tagTitle ? this.tagTitle : this.icon,
            href: this.href,
            target: this.target,
            rel: this.rel,
            download: this.download,
            dataEventType: this.dataEventtype ?? null,
            dataEvent: this.dataEvent ?? null,
            dataUid: this.dataUid ?? null,
          };
    const focusAttributes = this.focusable
      ? {}
      : {
          ariaHidden: true,
          tabIndex: -1,
        };
    return (
      <Host
        onClick={(event: Event) => {
          this.cmsButtonClickHandler(event);
        }}
        aria-hidden={this.disabled ? 'true' : null}
        class={this.hostClassMap()}
      >
        <TagType
          {...tagAttributes}
          {...focusAttributes}
          class={this.tagTypeClassMap()}
          hb-data-namespace={this.namespace}
          disabled={this.disabled}
          onFocus={() => {
            this.onFocus();
          }}
          onBlur={() => {
            this.onBlur();
          }}
        >
          {this.label}
          {this.icon ? (
            <cms-icon
              colorToken={this.iconColor ? this.iconColor : null}
              size={this.iconSize}
              icon={this.icon}
            />
          ) : null}
        </TagType>
      </Host>
    );
  }
}
